import React from "react";
import { HorizontalSelect } from "./HorizontalSelectField";
import { AnimatePresence, motion } from "framer-motion";

interface ToolParameterCardProps {
  label: string;
  description?: string;
  isExposed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  type?: string;
  optional?: boolean;
  showModeToggle?: boolean;
}

const parameterModeOptions = [
  { value: "llm", label: "Autofilled" },
  { value: "fixed", label: "Fixed" },
];

export const ToolParameterCard = ({
  label,
  description,
  isExposed,
  onToggle,
  children,
  type,
  optional,
  showModeToggle = true,
}: ToolParameterCardProps) => {
  const handleModeChange = (value: string | string[]) => {
    if (!showModeToggle) return;
    const newValue = Array.isArray(value) ? value[0] : value;
    const shouldBeExposed = newValue === "llm";
    if (shouldBeExposed !== isExposed) {
      onToggle();
    }
  };

  // For user-mode fields (showModeToggle=false), always show the input
  const shouldShowInput = !showModeToggle || !isExposed;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="p-3 overflow-hidden"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">{label}</p>
              {type && optional && (
                <span className="text-xs text-muted-foreground">
                  (optional)
                </span>
              )}
            </div>
            {description && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {description}
              </p>
            )}
          </div>
        </div>

        {showModeToggle && (
          <div className="flex-shrink-0">
            <HorizontalSelect
              options={parameterModeOptions}
              value={isExposed ? "llm" : "fixed"}
              onChange={handleModeChange}
            />
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {shouldShowInput && (
          <motion.div
            key="children"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full mt-3"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
